/**
 * Tenant consolidated-invoicing schedule edited on Settings → Billing (#3933).
 *
 * Shape matches `consolidatedInvoicing` on the tenant settings write schema:
 * `enabled` + `cycle`, with `weekday` (ISO 1 = Monday … 7 = Sunday) and
 * `cutoffTime` (`HH:mm`) required for weekly and biweekly, and an optional
 * Monday `biweeklyAnchorWeek`. The settings PATCH replaces this object whole,
 * so the payload always sends `enabled` and `cycle` together.
 */

import { intlLocale } from '@/utils/locale';

export const CONSOLIDATED_INVOICING_CYCLES = ['weekly', 'biweekly', 'monthly'] as const;
export type ConsolidatedInvoicingCycle = (typeof CONSOLIDATED_INVOICING_CYCLES)[number];

const CUTOFF_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface ConsolidatedInvoicingFormValue {
  enabled: boolean;
  cycle: ConsolidatedInvoicingCycle;
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  weekday: number;
  /** `HH:mm` in the tenant's local clock. */
  cutoffTime: string;
  /** `YYYY-MM-DD` Monday, or empty when the workshop has not picked an anchor. */
  biweeklyAnchorWeek: string;
}

/** Query for GET /invoices/consolidation/schedule-preview. Omitted anchor stays off the URL. */
export interface ConsolidationSchedulePreviewParams {
  cycle: ConsolidatedInvoicingCycle;
  weekday: number;
  cutoffTime: string;
  biweeklyAnchorWeek?: string;
}

export interface ConsolidatedInvoicingSettingsPayload {
  enabled: boolean;
  cycle: ConsolidatedInvoicingCycle;
  weekday?: number;
  cutoffTime?: string;
  biweeklyAnchorWeek?: string;
}

export type ConsolidatedInvoicingSaveError = 'cutoff' | 'weekday' | 'anchor';

/** Monday 09:00, off. Matches the schedule example the cutoff job is built around. */
export const DEFAULT_CONSOLIDATED_INVOICING_FORM: ConsolidatedInvoicingFormValue = {
  enabled: false,
  cycle: 'weekly',
  weekday: 1,
  cutoffTime: '09:00',
  biweeklyAnchorWeek: '',
};

export function normalizeCutoffTime(raw: string): string {
  const match = /^(\d{2}):(\d{2})/.exec(raw.trim());
  if (match === null) return raw.trim();
  const hours = match[1];
  const minutes = match[2];
  if (hours === undefined || minutes === undefined) return raw.trim();
  return `${hours}:${minutes}`;
}

function isCycle(value: unknown): value is ConsolidatedInvoicingCycle {
  return value === 'weekly' || value === 'biweekly' || value === 'monthly';
}

function isValidWeekday(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 7;
}

/** Monday in UTC, so a date does not flip in a timezone behind UTC. */
export function isIsoMonday(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day &&
    date.getUTCDay() === 1
  );
}

export function biweeklyAnchorError(value: ConsolidatedInvoicingFormValue): boolean {
  return value.cycle === 'biweekly' && value.biweeklyAnchorWeek !== '' && !isIsoMonday(value.biweeklyAnchorWeek);
}

export function consolidatedInvoicingSaveError(
  value: ConsolidatedInvoicingFormValue,
): ConsolidatedInvoicingSaveError | null {
  if (!isValidWeekday(value.weekday)) return 'weekday';
  if (!CUTOFF_TIME.test(normalizeCutoffTime(value.cutoffTime))) return 'cutoff';
  if (biweeklyAnchorError(value)) return 'anchor';
  return null;
}

/** Missing or unusable stored value becomes the default schedule, still disabled. */
export function parseConsolidatedInvoicingForm(raw: unknown): ConsolidatedInvoicingFormValue {
  const base = { ...DEFAULT_CONSOLIDATED_INVOICING_FORM };
  if (typeof raw !== 'object' || raw === null) return base;
  const record = raw as Record<string, unknown>;
  const weekdayRaw = record['weekday'];
  const cutoffRaw = record['cutoffTime'];
  const anchorRaw = record['biweeklyAnchorWeek'];
  return {
    enabled: record['enabled'] === true,
    cycle: isCycle(record['cycle']) ? record['cycle'] : base.cycle,
    weekday:
      typeof weekdayRaw === 'number' && isValidWeekday(weekdayRaw) ? weekdayRaw : base.weekday,
    cutoffTime:
      typeof cutoffRaw === 'string' && CUTOFF_TIME.test(cutoffRaw) ? cutoffRaw : base.cutoffTime,
    biweeklyAnchorWeek: typeof anchorRaw === 'string' && isIsoMonday(anchorRaw) ? anchorRaw : '',
  };
}

export function consolidatedInvoicingToPayload(
  value: ConsolidatedInvoicingFormValue,
): ConsolidatedInvoicingSettingsPayload {
  const payload: ConsolidatedInvoicingSettingsPayload = {
    enabled: value.enabled,
    cycle: value.cycle,
  };
  if (isValidWeekday(value.weekday)) {
    payload.weekday = value.weekday;
  }
  const cutoffTime = normalizeCutoffTime(value.cutoffTime);
  if (CUTOFF_TIME.test(cutoffTime)) {
    payload.cutoffTime = cutoffTime;
  }
  if (value.cycle === 'biweekly' && isIsoMonday(value.biweeklyAnchorWeek)) {
    payload.biweeklyAnchorWeek = value.biweeklyAnchorWeek;
  }
  return payload;
}

/**
 * Unsaved form values for the preview route. Null when the clock time or
 * weekday cannot be sent; an anchor that is not a Monday is left off so the
 * other fields still preview.
 */
export function consolidationPreviewParams(
  value: ConsolidatedInvoicingFormValue,
): ConsolidationSchedulePreviewParams | null {
  const error = consolidatedInvoicingSaveError(value);
  // A bad anchor still previews the rest of the schedule; the field shows the error.
  if (error === 'cutoff' || error === 'weekday') return null;
  const cutoffTime = normalizeCutoffTime(value.cutoffTime);
  const params: ConsolidationSchedulePreviewParams = {
    cycle: value.cycle,
    weekday: value.weekday,
    cutoffTime,
  };
  if (value.cycle === 'biweekly' && isIsoMonday(value.biweeklyAnchorWeek)) {
    params.biweeklyAnchorWeek = value.biweeklyAnchorWeek;
  }
  return params;
}

/** Calendar date `YYYY-MM-DD` in the UI locale, without a UTC-midnight shift. */
export function formatConsolidationIssueDate(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (match === null) return isoDate;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return isoDate;
  }
  return date.toLocaleDateString(intlLocale());
}
